import React from "react";
import { Flex, type BoxProps, SimpleGrid, Text, type TextProps } from "@chakra-ui/react";
import Image from "next/image";
import { type Customer } from "lib/sanity";
import { type Theme, type ThemeBgColor } from "contexts/color-theme";
import Balancer from "react-wrap-balancer";
import { Section } from "components/section";
import { getCustomerLogoUrl, getCustomerLogoStyles } from "utils";

const defaultMobileSlugs = new Set([
  "spotify",
  "nba",
  "petsmart",
  "weight-watchers",
  "ramp",
  "calendly",
  "grammarly",
  "gitlab",
]);

type StackedLogosProps = {
  title?: string;
  titleMaxW?: TextProps["maxW"];
  customerRows: Customer[][];
  mobileCustomers?: Customer[];
  theme?: Theme;
  themeBgColor?: ThemeBgColor;
} & BoxProps;

export function StackedLogos({
  title = "The world's most innovative companies choose Hightouch as their Composable CDP",
  titleMaxW = "2xl",
  customerRows,
  mobileCustomers,
  theme = "light",
  themeBgColor = "white",
  ...props
}: StackedLogosProps) {
  const allCustomers: Customer[] = [];
  for (const row of customerRows) for (const customer of row) allCustomers.push(customer);

  if (allCustomers.length === 0) {
    return null;
  }

  const mobileLogos = mobileCustomers ?? allCustomers.filter((customer) => defaultMobileSlugs.has(customer.slug));

  return (
    <Section theme={theme} themeBgColor={themeBgColor} {...props}>
      {title && (
        <Text
          color={`brand.text.${theme}`}
          textStyle={{ base: "headline3", md: "headline2" }}
          textAlign="center"
          maxW={titleMaxW}
          mx="auto"
          mb={{ base: "bigger", md: "biggest" }}
        >
          <Balancer>{title}</Balancer>
        </Text>
      )}

      <SimpleGrid
        display={{ base: "grid", md: "none" }}
        columns={4}
        alignItems="center"
        spacing={8}
        maxW={{ base: "100%", sm: "90%" }}
        mx="auto"
      >
        {mobileLogos.filter(Boolean).map((customer: Customer) => (
          <Flex
            key={customer.slug}
            pos="relative"
            align="center"
            justify="center"
            maxW="100%"
            w="100%"
            {...getCustomerLogoStyles(theme)}
          >
            <Image
              src={`${getCustomerLogoUrl(customer, theme)}?w=300`}
              alt={`${customer.name}.`}
              sizes="100%"
              width={5}
              height={2}
              style={{
                objectFit: "contain",
                objectPosition: "center",
                width: "100%",
                height: "unset",
                minWidth: "60px",
                maxWidth: "100px",
                maxHeight: "36px",
              }}
            />
          </Flex>
        ))}
      </SimpleGrid>

      <SimpleGrid
        display={{ base: "none", md: "grid" }}
        columns={{ base: 4, lg: 8 }}
        alignItems="center"
        spacing={8}
        maxW={{ base: "3xl", lg: "100%" }}
        mx="auto"
      >
        {allCustomers.filter(Boolean).map((customer: Customer) => (
          <Flex
            key={customer.slug}
            pos="relative"
            align="center"
            justify="center"
            w="100%"
            maxW="100%"
            {...getCustomerLogoStyles(theme)}
          >
            <Image
              src={`${getCustomerLogoUrl(customer, theme)}?w=300`}
              alt={`${customer.name}.`}
              sizes="100%"
              width={5}
              height={2}
              style={{
                objectFit: "contain",
                objectPosition: "center",
                width: "100%",
                height: "unset",
                minWidth: "60px",
                maxWidth: "100px",
                maxHeight: "36px",
              }}
            />
          </Flex>
        ))}
      </SimpleGrid>
    </Section>
  );
}
